import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { reservationRepository } from "../repositories";
import { defaultReservationFilters } from "../models";
const PAGE_SIZE = 25;
export const useReservationStore = defineStore("reservations", () => {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    const items = ref([]);
    const nextCursor = ref(null);
    const activeFilters = ref({ ...defaultReservationFilters });
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const error = ref(null);
    // ---------------------------------------------------------------------------
    // GETTERS
    // ---------------------------------------------------------------------------
    const hasMore = computed(() => nextCursor.value !== null);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    /**
     * Fetches page 1 for the given filters (or the current filters if omitted),
     * replacing whatever was previously loaded. Every call hits the network —
     * there's no TTL here since a filter change always needs a fresh server
     * query and there's no scenario where a stale-check should short-circuit it.
     */
    async function fetchFirstPage(filters = activeFilters.value) {
        activeFilters.value = filters;
        isLoading.value = true;
        error.value = null;
        items.value = [];
        nextCursor.value = null;
        try {
            const page = await reservationRepository.list(filters, null, PAGE_SIZE);
            items.value = page.items;
            nextCursor.value = page.nextCursor;
            return page.items;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "Error al cargar reservas";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function loadMore() {
        if (isLoading.value || isLoadingMore.value || !hasMore.value)
            return;
        isLoadingMore.value = true;
        error.value = null;
        try {
            const page = await reservationRepository.list(activeFilters.value, nextCursor.value, PAGE_SIZE);
            items.value = [...items.value, ...page.items];
            nextCursor.value = page.nextCursor;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Error al cargar más reservas";
            throw err;
        }
        finally {
            isLoadingMore.value = false;
        }
    }
    function $reset() {
        items.value = [];
        nextCursor.value = null;
        activeFilters.value = { ...defaultReservationFilters };
        isLoading.value = false;
        isLoadingMore.value = false;
        error.value = null;
    }
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        items,
        nextCursor,
        activeFilters,
        isLoading,
        isLoadingMore,
        error,
        hasMore,
        fetchFirstPage,
        loadMore,
        $reset,
    };
});
